import React from 'react';
import { X, Mail, Loader2, CheckCircle, XCircle } from 'lucide-react';

interface SendConsentAfterImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    isSending: boolean;
    totalImported: number;
    driversWithEmail: number;
    driversWithoutEmail: number;
}

const SendConsentAfterImportModal: React.FC<SendConsentAfterImportModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isSending,
    totalImported,
    driversWithEmail,
    driversWithoutEmail
}) => {
    const handleConfirm = async () => {
        await onConfirm();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: '#69BE2820' }}>
                            <Mail className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Send Consent Forms?</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isSending}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Would you like to send consent request emails to the newly imported drivers?
                    </p>

                    {/* Statistics */}
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Total drivers imported:</span>
                            <span className="text-sm font-semibold text-gray-900">{totalImported}</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="w-4 h-4" style={{ color: '#69BE28' }} />
                                <span className="text-sm text-gray-700">Drivers with email:</span>
                            </div>
                            <span className="text-sm font-semibold" style={{ color: '#69BE28' }}>
                                {driversWithEmail} (will receive forms)
                            </span>
                        </div>

                        {driversWithoutEmail > 0 && (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <XCircle className="w-4 h-4 text-orange-500" />
                                    <span className="text-sm text-gray-700">Drivers that may be skipped:</span>
                                </div>
                                <span className="text-sm font-semibold text-orange-600">
                                    {driversWithoutEmail}
                                </span>
                            </div>
                        )}
                    </div>

                    <p className="text-sm text-gray-600">
                        Consent form emails will be sent immediately to all drivers with valid email addresses.
                    </p>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSending}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                        No, Skip
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSending}
                        className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center"
                        style={{ backgroundColor: '#69BE28', borderColor: '#69BE28' }}
                    >
                        {isSending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isSending ? 'Sending...' : 'Yes, Send Consent Forms'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SendConsentAfterImportModal;
